import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Plus, BarChart3, Trash2, Pencil, X, Check, Search, HelpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const priorityLabel = (p) => (p >= 10 ? "Highest" : p >= 5 ? "High" : "Normal");
const PRIORITY_MAP = { Normal: 0, High: 5, Highest: 10 };

const TagMultiSelect = ({ options, selected, onChange, placeholder, testid }) => {
  const [q, setQ] = useState("");
  const sel = selected || [];
  const filtered = options.filter((o) => (o.label + " " + (o.sub || "")).toLowerCase().includes(q.toLowerCase()));
  const toggle = (v) => onChange(sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v]);
  const selectAllFiltered = () => onChange(Array.from(new Set([...sel, ...filtered.map((o) => o.value)])));
  const labelFor = (v) => options.find((o) => o.value === v)?.label || v;
  return (
    <div data-testid={testid}>
      {sel.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {sel.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-xs px-2 py-1 border border-emerald-500/30" data-testid={`${testid}-chip-${v}`}>
              {labelFor(v)}
              <button type="button" onClick={() => toggle(v)} className="hover:text-rose-500" data-testid={`${testid}-chip-remove-${v}`}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} className="pl-8" data-testid={`${testid}-search`} />
      </div>
      <div className="flex items-center justify-between mt-1.5 mb-1">
        <button type="button" onClick={selectAllFiltered} className="text-xs text-emerald-600 hover:underline" data-testid={`${testid}-select-all`}>Select all{q ? " (filtered)" : ""}</button>
        {sel.length > 0 && <button type="button" onClick={() => onChange([])} className="text-xs text-muted-foreground hover:underline" data-testid={`${testid}-clear`}>Clear</button>}
      </div>
      <div className="max-h-40 overflow-y-auto rounded-lg border border-border divide-y divide-border">
        {filtered.length === 0 ? <p className="text-xs text-muted-foreground p-3 text-center">No matches</p> :
          filtered.map((o) => {
            const on = sel.includes(o.value);
            return (
              <button type="button" key={o.value} onClick={() => toggle(o.value)} className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-secondary/60 ${on ? "bg-emerald-500/10" : ""}`} data-testid={`${testid}-opt-${o.value}`}>
                <span className="min-w-0 truncate">{o.label}{o.sub ? <span className="text-xs text-muted-foreground ml-1">· {o.sub}</span> : null}</span>
                {on && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
              </button>
            );
          })}
      </div>
    </div>
  );
};

// B-06: product picker is server-search-backed (GET /products/search) instead of loading the whole catalogue —
// safe at any catalogue size (small store or 8,000+). Selected chips render from `resolveLabel`, which the
// parent keeps populated for every id ever selected or already saved, so previously-chosen products still show
// correctly even when they're not part of the current search results (editing an old promotion, etc). A simple
// per-request sequence number (`seqRef`) discards any search response that isn't the latest one in flight, so a
// slow earlier request can never clobber a faster later one. Parent (non-sellable variant group) products are
// filtered out of results — they aren't saleable on their own and shouldn't become a promotion target.
const ProductTagMultiSelect = ({ selected, resolveLabel, onChange, testid }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const seqRef = useRef(0);
  const sel = selected || [];
  useEffect(() => {
    const term = q.trim();
    if (!term) { setResults([]); return; }
    const mySeq = ++seqRef.current;
    const t = setTimeout(() => {
      api.get("/products/search", { params: { q: term, limit: 40 } })
        .then((r) => { if (seqRef.current === mySeq) setResults((r.data || []).filter((p) => !p.is_parent)); })
        .catch(() => { if (seqRef.current === mySeq) setResults([]); });
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const toggle = (p) => onChange(sel.includes(p.id) ? sel.filter((x) => x !== p.id) : [...sel, p.id], [p]);
  const selectAllFiltered = () => {
    const toAdd = results.filter((p) => !sel.includes(p.id));
    if (toAdd.length) onChange([...sel, ...toAdd.map((p) => p.id)], toAdd);
  };
  return (
    <div data-testid={testid}>
      {sel.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {sel.map((id) => {
            const r = resolveLabel(id);
            return (
              <span key={id} className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-xs px-2 py-1 border border-emerald-500/30" data-testid={`${testid}-chip-${id}`}>
                {r.name}{r.sku ? <span className="opacity-70 ml-1">· {r.sku}</span> : null}
                <button type="button" onClick={() => onChange(sel.filter((x) => x !== id), [])} className="hover:text-rose-500" data-testid={`${testid}-chip-remove-${id}`}><X className="w-3 h-3" /></button>
              </span>
            );
          })}
        </div>
      )}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products by name, SKU or barcode…" className="pl-8" data-testid={`${testid}-search`} />
      </div>
      <div className="flex items-center justify-between mt-1.5 mb-1">
        {results.length > 0 && <button type="button" onClick={selectAllFiltered} className="text-xs text-emerald-600 hover:underline" data-testid={`${testid}-select-all`}>Select all {results.length} shown</button>}
        {sel.length > 0 && <button type="button" onClick={() => onChange([], [])} className="text-xs text-muted-foreground hover:underline" data-testid={`${testid}-clear`}>Clear all selected</button>}
      </div>
      <div className="max-h-40 overflow-y-auto rounded-lg border border-border divide-y divide-border">
        {!q.trim() ? <p className="text-xs text-muted-foreground p-3 text-center">Type to search products…</p> :
          results.length === 0 ? <p className="text-xs text-muted-foreground p-3 text-center">No matches</p> :
          results.map((p) => {
            const on = sel.includes(p.id);
            return (
              <button type="button" key={p.id} onClick={() => toggle(p)} className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-secondary/60 ${on ? "bg-emerald-500/10" : ""}`} data-testid={`${testid}-opt-${p.id}`}>
                <span className="min-w-0 truncate">{p.name}{p.sku ? <span className="text-xs text-muted-foreground ml-1">· {p.sku}</span> : null}</span>
                {on && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
              </button>
            );
          })}
      </div>
    </div>
  );
};

const blank = {
  name: "", description: "", start_at: "", end_at: "", active: true,
  discount_type: "percent", discount_value: 10, scope: "entire",
  product_ids: [], categories: [], min_purchase: 0,
  customer_scope: "all", customer_ids: [], priority: 0,
};

export default function Promotions() {
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [report, setReport] = useState(null);
  // B-06: no full product list is ever loaded. `productNames` only ever holds display metadata for products a
  // promotion has actually referenced (list cards) or the editor has resolved (existing selections + fresh
  // picks) — bounded by real usage, not catalogue size. `categories` comes from a tiny dedicated distinct-value
  // endpoint instead of being derived from a full product array.
  const [productNames, setProductNames] = useState({});
  const [categories, setCategories] = useState([]);
  const mergeProductNames = (rows) => { if (rows?.length) setProductNames((m) => { const n = { ...m }; rows.forEach((r) => { n[r.id] = { name: r.name, sku: r.sku }; }); return n; }); };

  const load = () => api.get("/promotions").then(async (r) => {
    setList(r.data);
    const ids = Array.from(new Set((r.data || []).filter((p) => p.scope === "products").flatMap((p) => p.product_ids || [])));
    if (ids.length) {
      try { const { data } = await api.get("/products", { params: { ids: ids.slice(0, 500).join(","), fields: "lite", archived: "all" } }); mergeProductNames(data); }
      catch { /* names fall back to raw id in the UI; selections themselves are unaffected */ }
    }
  }).catch(() => {});
  useEffect(() => {
    load();
    api.get("/products/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Enter a promotion name"); return; }
    const body = {
      ...form,
      discount_value: parseFloat(form.discount_value || 0),
      min_purchase: parseFloat(form.min_purchase || 0),
      priority: parseInt(form.priority || 0),
      start_at: form.start_at || null, end_at: form.end_at || null,
    };
    try {
      if (form.id) await api.put(`/promotions/${form.id}`, body);
      else await api.post("/promotions", body);
      toast.success("Promotion saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggle = async (p) => {
    try { await api.post(`/promotions/${p.id}/toggle`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete promotion "${p.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/promotions/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openReport = async (p) => {
    try { const { data } = await api.get(`/promotions/${p.id}/report`); setReport(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const nameForProduct = (id) => productNames[id]?.name || id;
  const targetSummary = (p) => {
    if (p.scope === "products") {
      const arr = p.product_ids || [];
      if (!arr.length) return "No products";
      return arr.slice(0, 3).map(nameForProduct).join(", ") + (arr.length > 3 ? ` +${arr.length - 3} more` : "");
    }
    if (p.scope === "categories") {
      const arr = p.categories || [];
      if (!arr.length) return "No categories";
      return arr.slice(0, 3).join(", ") + (arr.length > 3 ? ` +${arr.length - 3} more` : "");
    }
    return "Entire sale";
  };

  const now = new Date().toISOString();
  const statusOf = (p) => {
    if (!p.active) return { label: "Inactive", cls: "bg-muted text-muted-foreground border-border" };
    if (p.start_at && now < p.start_at) return { label: "Scheduled", cls: "bg-sky-500/15 text-sky-600 border-sky-500/30" };
    if (p.end_at && now > p.end_at) return { label: "Expired", cls: "bg-rose-500/15 text-rose-600 border-rose-500/30" };
    return { label: "Active", cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" };
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="promotions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-extrabold flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-violet-500" /> Promotions / Specials
          </h1>
          <p className="text-muted-foreground">Create scheduled promotions that apply automatically at checkout.</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs" data-testid="promo-help-links">
            {[["create-special", "How do I create a Special?"], ["manage-special", "How do I pause or stop a Special?"], ["negotiated-price", "How does Negotiated Price work?"], ["discount-approvals", "How do approval limits work?"]].map(([slug, label]) => (
              <button key={slug} type="button" onClick={() => nav(`/help?topic=${slug}`)} className="inline-flex items-center gap-1 text-emerald-600 hover:underline" data-testid={`promo-help-${slug}`}><HelpCircle className="w-3.5 h-3.5" /> {label}</button>
            ))}
          </div>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setForm(blank); setOpen(true); }} data-testid="promo-new-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Promotion
        </Button>
      </div>

      {list.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="promo-empty">No promotions yet. Create your first one.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {list.map((p) => {
            const st = statusOf(p);
            return (
              <Card key={p.id} className="p-4" data-testid={`promo-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-head font-bold truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.description || "-"}</p>
                  </div>
                  <Badge className={`text-[10px] shrink-0 ${st.cls}`} data-testid={`promo-status-${p.id}`}>{st.label}</Badge>
                </div>
                <div className="mt-2 text-sm flex flex-wrap gap-x-4 gap-y-1">
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">{p.discount_type === "percent" ? `${p.discount_value}% off` : `${money(p.discount_value)} off`}</span>
                  <span className="text-muted-foreground truncate max-w-[60%]" title={targetSummary(p)} data-testid={`promo-target-${p.id}`}>{targetSummary(p)}</span>
                  <span className="text-muted-foreground">Priority {p.priority}</span>
                  {p.min_purchase > 0 && <span className="text-muted-foreground">Min {money(p.min_purchase)}</span>}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {p.start_at ? new Date(p.start_at).toLocaleDateString() : "Always"} → {p.end_at ? new Date(p.end_at).toLocaleDateString() : "No end"}
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <Switch checked={p.active} onCheckedChange={() => toggle(p)} data-testid={`promo-toggle-${p.id}`} />
                  <Button size="sm" variant="ghost" onClick={async () => {
                    const missing = (p.product_ids || []).filter((id) => !productNames[id]);
                    if (p.scope === "products" && missing.length) {
                      try { const { data } = await api.get("/products", { params: { ids: missing.slice(0, 500).join(","), fields: "lite", archived: "all" } }); mergeProductNames(data); } catch { /* chips fall back to raw id */ }
                    }
                    setForm({ ...blank, ...p });
                    setOpen(true);
                  }} data-testid={`promo-edit-${p.id}`}><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => openReport(p)} data-testid={`promo-report-${p.id}`}><BarChart3 className="w-4 h-4 mr-1" /> Report</Button>
                  <Button size="sm" variant="ghost" className="text-rose-500 ml-auto" onClick={() => remove(p)} data-testid={`promo-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="promo-form">
          <DialogHeader><DialogTitle className="font-head">{form.id ? "Edit" : "New"} Promotion</DialogTitle>
            <DialogDescription>Buy-X-Get-Y is not part of this release.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Christmas Special" data-testid="promo-f-name" /></div>
            <div className="col-span-2"><Label className="text-xs">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="promo-f-desc" /></div>
            <div><Label className="text-xs">Start</Label><Input type="datetime-local" value={form.start_at || ""} onChange={(e) => setForm({ ...form, start_at: e.target.value })} data-testid="promo-f-start" /></div>
            <div><Label className="text-xs">End</Label><Input type="datetime-local" value={form.end_at || ""} onChange={(e) => setForm({ ...form, end_at: e.target.value })} data-testid="promo-f-end" /></div>
            <div><Label className="text-xs">Discount Type</Label>
              <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="promo-f-dtype">
                <option value="percent">Percentage</option><option value="fixed">Fixed amount</option>
              </select></div>
            <div><Label className="text-xs">Value</Label><Input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="mono" data-testid="promo-f-dvalue" /></div>
            <div><Label className="text-xs">Applies To</Label>
              <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="promo-f-scope">
                <option value="entire">Entire sale</option><option value="products">Selected products</option><option value="categories">Selected categories</option>
              </select></div>
            <div><Label className="text-xs">Min Purchase</Label><Input type="number" value={form.min_purchase} onChange={(e) => setForm({ ...form, min_purchase: e.target.value })} className="mono" data-testid="promo-f-min" /></div>
            {form.scope === "products" && <div className="col-span-2"><Label className="text-xs">Products</Label><ProductTagMultiSelect testid="promo-f-products" selected={form.product_ids || []} resolveLabel={(id) => productNames[id] || { name: id, sku: "" }} onChange={(ids, picked) => { setForm((f) => ({ ...f, product_ids: ids })); mergeProductNames(picked); }} /></div>}
            {form.scope === "categories" && <div className="col-span-2"><Label className="text-xs">Categories</Label><TagMultiSelect testid="promo-f-categories" placeholder="Search categories…" options={categories.map((c) => ({ value: c, label: c }))} selected={form.categories || []} onChange={(v) => setForm({ ...form, categories: v })} /></div>}
            <div><Label className="text-xs">Customers</Label>
              <select value={form.customer_scope} onChange={(e) => setForm({ ...form, customer_scope: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="promo-f-custscope">
                <option value="all">All customers</option><option value="selected">Selected customers</option>
              </select></div>
            <div><Label className="text-xs">Priority</Label>
              <select value={priorityLabel(parseInt(form.priority || 0))} onChange={(e) => setForm({ ...form, priority: PRIORITY_MAP[e.target.value] })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="promo-f-priority">
                <option value="Normal">Normal</option><option value="High">High</option><option value="Highest">Highest</option>
              </select></div>
            {form.customer_scope === "selected" && <div className="col-span-2"><Label className="text-xs">Customer IDs (comma-separated)</Label><Input value={(form.customer_ids || []).join(",")} onChange={(e) => setForm({ ...form, customer_ids: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} data-testid="promo-f-customers" /></div>}
            <div className="col-span-2 flex items-center justify-between rounded-xl border border-border p-3">
              <span className="text-sm font-medium">Active</span>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="promo-f-active" />
            </div>
          </div>
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="promo-save">Save Promotion</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!report} onOpenChange={(o) => { if (!o) setReport(null); }}>
        <DialogContent className="max-w-sm" data-testid="promo-report-modal">
          <DialogHeader><DialogTitle className="font-head">Report: {report?.name}</DialogTitle></DialogHeader>
          {report && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Transactions</span><span className="mono font-bold">{report.transactions}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total discount given</span><span className="mono font-bold">{money(report.total_discount)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Revenue generated</span><span className="mono font-bold">{money(report.revenue)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Customers</span><span className="mono font-bold">{report.customer_count}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span className="font-semibold">{report.active ? "Active" : "Inactive"}</span></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
